/**
 * Hook para gerenciamento de vínculos entre itens do catálogo e workspaces
 * Gerencia as tabelas de junção: crm_category_workspaces, crm_product_workspaces, crm_price_table_workspaces
 * 
 * Versão: 1.0.0
 * - CRUD para vínculos de categorias, produtos e tabelas de preço com workspaces
 */
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

// Tipos para os vínculos
type ItemType = 'category' | 'product' | 'price_table';

// Tipo para vínculo de workspace
interface WorkspaceLink {
  id: string;
  workspace_id: string;
}

// Input para atualizar workspaces de um item
interface UpdateItemWorkspacesInput {
  /** Tipo do item */
  itemType: ItemType;
  /** ID do item (categoria, produto ou tabela de preço) */
  itemId: string;
  /** IDs dos workspaces que devem estar vinculados */
  workspaceIds: string[];
}

/**
 * Hook para gerenciar vínculos de itens com workspaces
 * Implementa operação de sync: remove vínculos antigos e cria novos
 */
export function useCrmItemWorkspaces() {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Mutation para sincronizar workspaces de um item
   * Remove vínculos não presentes e cria novos vínculos
   */
  const updateItemWorkspaces = useMutation({
    mutationFn: async ({ itemType, itemId, workspaceIds }: UpdateItemWorkspacesInput) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');
      if (workspaceIds.length === 0) {
        throw new Error('É necessário vincular pelo menos um workspace');
      }

      let currentLinks: WorkspaceLink[] = [];

      // 1. Buscar vínculos atuais baseado no tipo
      if (itemType === 'category') {
        const { data, error } = await supabase
          .from('crm_category_workspaces')
          .select('id, workspace_id')
          .eq('category_id', itemId);
        if (error) throw error;
        currentLinks = data || [];
      } else if (itemType === 'product') {
        const { data, error } = await supabase
          .from('crm_product_workspaces')
          .select('id, workspace_id')
          .eq('product_id', itemId);
        if (error) throw error;
        currentLinks = data || [];
      } else if (itemType === 'price_table') {
        const { data, error } = await supabase
          .from('crm_price_table_workspaces')
          .select('id, workspace_id')
          .eq('price_table_id', itemId);
        if (error) throw error;
        currentLinks = data || [];
      }

      const currentWorkspaceIds = currentLinks.map((l) => l.workspace_id);

      // 2. Determinar quais remover e quais adicionar
      const toRemove = currentLinks.filter((l) => !workspaceIds.includes(l.workspace_id));
      const toAdd = workspaceIds.filter((wsId) => !currentWorkspaceIds.includes(wsId));

      // 3. Remover vínculos antigos
      if (toRemove.length > 0) {
        const removeIds = toRemove.map((l) => l.id);
        
        if (itemType === 'category') {
          const { error } = await supabase
            .from('crm_category_workspaces')
            .delete()
            .in('id', removeIds);
          if (error) throw error;
        } else if (itemType === 'product') {
          const { error } = await supabase
            .from('crm_product_workspaces')
            .delete()
            .in('id', removeIds);
          if (error) throw error;
        } else if (itemType === 'price_table') {
          const { error } = await supabase
            .from('crm_price_table_workspaces')
            .delete()
            .in('id', removeIds);
          if (error) throw error;
        }
      }

      // 4. Criar novos vínculos
      if (toAdd.length > 0) {
        if (itemType === 'category') {
          const newLinks = toAdd.map((workspaceId) => ({
            category_id: itemId,
            workspace_id: workspaceId,
            tenant_id: currentTenant.id,
          }));
          const { error } = await supabase
            .from('crm_category_workspaces')
            .insert(newLinks);
          if (error) throw error;
        } else if (itemType === 'product') {
          const newLinks = toAdd.map((workspaceId) => ({
            product_id: itemId,
            workspace_id: workspaceId,
            tenant_id: currentTenant.id,
          }));
          const { error } = await supabase
            .from('crm_product_workspaces')
            .insert(newLinks);
          if (error) throw error;
        } else if (itemType === 'price_table') {
          const newLinks = toAdd.map((workspaceId) => ({
            price_table_id: itemId,
            workspace_id: workspaceId,
            tenant_id: currentTenant.id,
          }));
          const { error } = await supabase
            .from('crm_price_table_workspaces')
            .insert(newLinks);
          if (error) throw error;
        }
      }

      return { removed: toRemove.length, added: toAdd.length };
    },
    onSuccess: (_, variables) => {
      // Invalidar queries relacionadas
      if (variables.itemType === 'category') {
        queryClient.invalidateQueries({ queryKey: ['crm-categories'] });
      } else if (variables.itemType === 'product') {
        queryClient.invalidateQueries({ queryKey: ['crm-products'] });
      } else if (variables.itemType === 'price_table') {
        queryClient.invalidateQueries({ queryKey: ['crm-price-tables'] });
      }
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar workspaces: ${error.message}`);
    },
  });

  /**
   * Buscar workspaces vinculados a um item
   */
  const fetchItemWorkspaces = async (itemType: ItemType, itemId: string): Promise<string[]> => {
    let data: { workspace_id: string }[] | null = null;

    if (itemType === 'category') {
      const result = await supabase
        .from('crm_category_workspaces')
        .select('workspace_id')
        .eq('category_id', itemId);
      if (result.error) throw result.error;
      data = result.data;
    } else if (itemType === 'product') {
      const result = await supabase
        .from('crm_product_workspaces')
        .select('workspace_id')
        .eq('product_id', itemId);
      if (result.error) throw result.error;
      data = result.data;
    } else if (itemType === 'price_table') {
      const result = await supabase
        .from('crm_price_table_workspaces')
        .select('workspace_id')
        .eq('price_table_id', itemId);
      if (result.error) throw result.error;
      data = result.data;
    }

    return data?.map((d) => d.workspace_id) || [];
  };

  return {
    updateItemWorkspaces,
    fetchItemWorkspaces,
  };
}

/**
 * Hook para buscar workspaces de um item específico
 */
export function useItemWorkspaces(itemType: ItemType, itemId: string | null) {
  return useQuery({
    queryKey: ['item-workspaces', itemType, itemId],
    queryFn: async () => {
      if (!itemId) return [];

      let data: { workspace_id: string }[] | null = null;

      if (itemType === 'category') {
        const result = await supabase
          .from('crm_category_workspaces')
          .select('workspace_id')
          .eq('category_id', itemId);
        if (result.error) throw result.error;
        data = result.data;
      } else if (itemType === 'product') {
        const result = await supabase
          .from('crm_product_workspaces')
          .select('workspace_id')
          .eq('product_id', itemId);
        if (result.error) throw result.error;
        data = result.data;
      } else if (itemType === 'price_table') {
        const result = await supabase
          .from('crm_price_table_workspaces')
          .select('workspace_id')
          .eq('price_table_id', itemId);
        if (result.error) throw result.error;
        data = result.data;
      }

      return data?.map((d) => d.workspace_id) || [];
    },
    enabled: !!itemId,
  });
}
